import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Check, Lock, Wind } from 'lucide-react';
import { Button, cn } from '@ai-tutor/ui';
import { useTransientFlag } from '@/hooks/ui/useTransientFlag';

export interface Worry {
  id: string;
  text: string;
  createdAt: number;
}

interface WorryDumpProps {
  worries: Worry[];
  onAdd: (text: string) => void;
  onLetGo: (id: string) => void;
  textareaId?: string;
  /**
   * Put the cursor in the box on arrival - the Worry dump page, where writing
   * is the only thing to do. Done on mount rather than with `autoFocus`, so
   * it fires once, when the page opens, and never steals focus later.
   */
  focusOnMount?: boolean;
}

/**
 * Write the worry down, then set it aside before studying.
 *
 * Expressive writing before a test measurably helps anxious students
 * (Ramirez & Beilock, 2011); "set it aside" is worry postponement. The notes
 * are kept, out of sight, so they can be revisited later and let go of one
 * at a time. They never leave the device.
 */
export function WorryDump({
  worries,
  onAdd,
  onLetGo,
  textareaId,
  focusOnMount = false,
}: WorryDumpProps) {
  const fallbackId = useId();
  const fieldId = textareaId ?? fallbackId;
  const [text, setText] = useState('');
  const fieldRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (focusOnMount) fieldRef.current?.focus();
    // Mount only: this is about arriving on the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // A bump counter, not the text: setting the same worry twice re-arms it.
  const [setCount, setSetCount] = useState(0);
  const justSet = useTransientFlag(setCount, 2400, setCount > 0);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    onAdd(text.trim());
    setText('');
    setSetCount(n => n + 1);
  };

  return (
    <div className='space-y-4'>
      <form onSubmit={submit} className='space-y-3'>
        <label
          htmlFor={fieldId}
          className='block text-sm text-muted-foreground'
        >
          What&apos;s on your mind? Write it here so your head doesn&apos;t have
          to hold it.
        </label>
        <textarea
          id={fieldId}
          value={text}
          onChange={e => setText(e.target.value)}
          rows={6}
          ref={fieldRef}
          className='w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          placeholder='The chemistry test on Friday…'
        />
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <span className='flex items-center gap-1.5 text-xs text-muted-foreground'>
            <Lock className='h-3 w-3' aria-hidden='true' />
            Stays on this device
          </span>
          <Button
            type='submit'
            size='sm'
            className='max-sm:h-11'
            disabled={!text.trim()}
          >
            Set it aside
          </Button>
        </div>
      </form>

      <p
        aria-live='polite'
        className={cn(
          'flex items-center gap-1.5 text-sm text-muted-foreground transition-opacity duration-base',
          justSet ? 'opacity-100' : 'opacity-0'
        )}
      >
        {justSet && (
          <>
            <Check className='h-4 w-4' aria-hidden='true' />
            Set aside. You can come back to it later.
          </>
        )}
      </p>

      {worries.length > 0 && <SetAside worries={worries} onLetGo={onLetGo} />}
    </div>
  );
}

function SetAside({
  worries,
  onLetGo,
}: {
  worries: Worry[];
  onLetGo: (id: string) => void;
}) {
  const count = worries.length;
  return (
    <details className='group rounded-lg border border-border/60 bg-background/40'>
      <summary className='cursor-pointer list-none px-3 py-2 text-sm font-medium text-foreground marker:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'>
        {count} {count === 1 ? 'thought' : 'thoughts'} set aside
        <span className='ml-1 text-muted-foreground group-open:hidden'>
          · Revisit
        </span>
      </summary>
      <ul className='space-y-2 border-t border-border/60 px-3 py-3'>
        {worries.map(worry => (
          <li key={worry.id} className='flex items-start justify-between gap-3'>
            <span className='text-sm text-foreground'>{worry.text}</span>
            <Button
              variant='ghost'
              size='sm'
              className='shrink-0 max-sm:h-11'
              onClick={() => onLetGo(worry.id)}
              aria-label={`Let go of: ${worry.text}`}
            >
              <Wind className='mr-1 h-3.5 w-3.5' aria-hidden='true' />
              Let go
            </Button>
          </li>
        ))}
      </ul>
    </details>
  );
}
